import { useQuery } from "@apollo/client/react";
import {
  FeedbackGivenOnType,
  GetArticleData,
  GetArticleDataVariables,
  GetArticleParentFolder,
  GetArticleParentFolderVariables,
} from "__generated__/types";
import { useAuth } from "common/auth/Auth";
import { Author } from "common/components/Author";
import { MultilineText, ofMarkdown } from "common/components/MultilineText";
import { NoDataError } from "common/components/NoDataError";
import { SimpleErrorMessage } from "common/components/SimpleErrorMessage";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { TallLayout } from "common/components/layouts/TallLayout";
import { Markdown } from "common/components/markdown/Markdown";
import { config } from "common/config";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { parseErrorFromGraphql } from "common/graphql/utils";
import { getSyllabus } from "common/hooks/useBreadcrumbs";
import { useMyExamSyllabus } from "common/hooks/useMyExamSyllabus";
import { FolderNavigation } from "common/navigation/FolderNavigation";
import { urls } from "common/urls";
import { Article } from "domain/article/Article";
import { folderWithElementsFragment } from "domain/common/queries";
import {
  getParentFromContentContext,
  isSyllabusForStudent,
} from "domain/common/utils";
import { exampleAuthor } from "domain/constants";
import { FeedbackForm } from "domain/feedback/FeedbackForm";
import gql from "graphql-tag";
import * as React from "react";
import { Link, useParams } from "react-router-dom";

const DATA_QUERY = gql`
  query GetArticleData($articleId: ID!) {
    articleById(id: $articleId) {
      id
      title
      body {
        content
        isMarkdown
      }
      relevantContent {
        content
        isMarkdown
      }
      youtubeVideoId
      canUpdateDetails {
        canPerform
      }
      canCreateProgression {
        canPerform
      }
      context {
        id
        title
      }
    }
  }
`;

const FOLDER_ELEMENT_QUERY = gql`
  query GetArticleParentFolder($folderId: ID!) {
    folderById(id: $folderId) {
      ...GetFolderElementsFragment
    }
  }
  ${folderWithElementsFragment}
`;

type RouteProps = {
  articleId: string;
};

export const ArticlePage: React.FC = () => {
  const { articleId } = useParams<RouteProps>();
  const me = useAuth();
  const { myExamSyllabi } = useMyExamSyllabus();

  if (!articleId) {
    throw new Error("Unexpected error: Missing articleId");
  }

  const { data, loadingOrError } = useSimpleQuery<
    GetArticleData,
    GetArticleDataVariables
  >(DATA_QUERY, {
    variables: {
      articleId,
    },
  });

  const parentId = getParentFromContentContext(data?.articleById?.context)?.id;

  const { data: folderData, error: folderError } = useQuery<
    GetArticleParentFolder,
    GetArticleParentFolderVariables
  >(FOLDER_ELEMENT_QUERY, {
    skip: !parentId,
    variables: {
      folderId: parentId || "",
    },
  });

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (folderError) {
    return (
      <SimpleErrorMessage>
        {parseErrorFromGraphql(folderError)}
      </SimpleErrorMessage>
    );
  }

  if (!data || !data.articleById) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  const {
    title,
    context,
    body,
    canUpdateDetails,
    canCreateProgression,
    relevantContent,
    youtubeVideoId,
  } = data.articleById;

  const relevantContentForArticle = relevantContent?.content
    ? ofMarkdown(relevantContent.content)
    : null;

  const canGiveFeedback = me.me?.menu.feedback;
  const folderElementId = folderData?.folderById?.elements.find(
    (e) => e.content.id === articleId,
  )?.id;

  const tabs = [];

  if (relevantContentForArticle) {
    tabs.push({
      title: "Relevant innhold",
      content: <Markdown {...relevantContentForArticle} openLinksSeparately />,
    });
  }

  // TODO: Extract this to Article instead?
  const isSyllabus = isSyllabusForStudent(myExamSyllabi, getSyllabus(context));

  return (
    <TallLayout context={context ?? []}>
      <Article
        title={title}
        youtubeVideoId={youtubeVideoId}
        tabs={tabs}
        isSyllabus={isSyllabus && canCreateProgression.canPerform}
        id={articleId}
        containerType="Folder"
      >
        {config.toggles.showExampleAuthor && <Author name={exampleAuthor} />}
        {body && <MultilineText {...body} maxWidth openLinksSeparately />}
        {canGiveFeedback && folderElementId && (
          <FeedbackForm
            givenOnReferenceId={folderElementId}
            givenOnType={FeedbackGivenOnType.FolderElement}
          />
        )}
        <FolderNavigation
          currentElementId={articleId}
          folderElements={
            folderData?.folderById?.elements.map((e) => ({
              title: e.content.title,
              __typename: e.content.__typename,
              id: e.content.id,
            })) ?? []
          }
        />
        {canUpdateDetails.canPerform && (
          <Link to={urls.articleAdminEdit(articleId)}>Rediger artikkel</Link>
        )}
      </Article>
    </TallLayout>
  );
};
